import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { expenseService, walletService, Expense } from '@/lib/storage';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { motion } from 'framer-motion';
import {
  Wallet,
  IndianRupee,
  CheckCircle2,
  XCircle,
  Clock,
  ArrowDownLeft,
  Loader2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import ExpenseModal from '@/components/ExpenseModal';

const statusIcon: Record<string, typeof Clock> = {
  pending: Clock,
  approved: CheckCircle2,
  rejected: XCircle,
};

const statusClass: Record<string, string> = {
  pending: 'text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/30',
  approved: 'text-emerald-600 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/30',
  rejected: 'text-rose-600 dark:text-rose-400 bg-rose-100 dark:bg-rose-900/30',
};

export default function StaffWalletPage() {
  const { session } = useAuth();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [balance, setBalance] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [editExpense, setEditExpense] = useState<Expense | null>(null);

  const fetchData = async (silent = false) => {
    if (!session?.userId) return;
    if (!silent) setLoading(true);
    try {
      const [expenseData, walletData] = await Promise.all([
        expenseService.getAll({ staffId: session.userId }),
        walletService.getBalance(session.userId),
      ]);
      setExpenses(expenseData);
      setBalance(walletData.balance);
    } catch (error) {
      console.error('Error fetching wallet data:', error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(() => fetchData(true), 10000);
    return () => clearInterval(interval);
  }, [session?.userId]);

  const approvedTotal = expenses.filter(e => e.status === 'approved').reduce((s, e) => s + e.amount, 0);
  const pendingTotal = expenses.filter(e => e.status === 'pending').reduce((s, e) => s + e.amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-3xl mx-auto space-y-6"
    >
      {/* Balance Card */}
      <Card className="relative overflow-hidden border-amber-200/50 dark:border-amber-800/40">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-amber-400/5 to-transparent" />
        <CardContent className="p-6 relative">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground flex items-center gap-1.5">
                <Wallet className="w-4 h-4" />
                Wallet Balance
              </p>
              <p className="text-3xl font-black mt-2 text-amber-700 dark:text-amber-400">
                ₹{balance.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
            </div>
            <div className="w-16 h-16 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
              <IndianRupee className="w-8 h-8 text-amber-600 dark:text-amber-400" />
            </div>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-4 mt-4 text-xs">
            <div className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 font-semibold bg-emerald-50 dark:bg-emerald-950/40 px-2 py-1 rounded-lg">
              <ArrowDownLeft className="w-3.5 h-3.5" />
              <span>₹{approvedTotal.toLocaleString('en-IN')} approved</span>
            </div>
            <div className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400 font-semibold bg-amber-50 dark:bg-amber-950/40 px-2 py-1 rounded-lg">
              <Clock className="w-3.5 h-3.5" />
              <span>₹{pendingTotal.toLocaleString('en-IN')} pending</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statement */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base font-bold flex items-center gap-2">
            <IndianRupee className="w-5 h-5 text-amber-500" />
            Expense Statement
            <span className="ml-auto text-xs font-normal text-muted-foreground">{expenses.length} entries</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          {expenses.length === 0 ? (
            <div className="flex flex-col items-center py-10 text-muted-foreground">
              <Wallet className="w-10 h-10 mb-3 opacity-40" />
              <p className="text-sm font-medium">No expenses yet</p>
              <p className="text-xs mt-1">Submit an expense after checking out to see it here.</p>
            </div>
          ) : (
            <div className="space-y-1">
              {expenses.map((exp, idx) => {
                const StatusIcon = statusIcon[exp.status];
                return (
                  <div
                    key={exp.id}
                    className={cn(
                      "flex items-center gap-3 p-3.5 rounded-xl transition-colors",
                      exp.status === 'pending' ? 'bg-amber-50/50 dark:bg-amber-950/10 border border-amber-200/30 dark:border-amber-800/20' : 'hover:bg-slate-50 dark:hover:bg-slate-900/30'
                    )}
                  >
                    <div className={cn("w-9 h-9 rounded-lg flex items-center justify-center shrink-0", statusClass[exp.status])}>
                      <StatusIcon className="w-4.5 h-4.5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-bold">₹{exp.amount.toLocaleString('en-IN')}</span>
                        <span className={cn(
                          "text-[10px] px-1.5 py-0.5 rounded-full font-semibold",
                          exp.status === 'pending' && 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400',
                          exp.status === 'approved' && 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400',
                          exp.status === 'rejected' && 'bg-rose-100 dark:bg-rose-900/30 text-rose-700 dark:text-rose-400',
                        )}>
                          {exp.status.charAt(0).toUpperCase() + exp.status.slice(1)}
                        </span>
                      </div>
                      {exp.remark && (
                        <p className="text-xs text-muted-foreground truncate mt-0.5">{exp.remark}</p>
                      )}
                      <div className="flex items-center gap-3 mt-0.5">
                        <p className="text-[10px] text-muted-foreground">
                          {new Date(exp.date + 'T00:00:00').toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                        </p>
                        {exp.adminRemark && (
                          <p className="text-[10px] text-muted-foreground">Admin: {exp.adminRemark}</p>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      {exp.status === 'approved' && (
                        exp.remark && (exp.remark.includes('Wage') || exp.remark.includes('Salary') || exp.remark.includes('Credit')) ? (
                          <p className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">+₹{exp.amount.toLocaleString('en-IN')}</p>
                        ) : (
                          <p className="text-xs font-semibold text-rose-600 dark:text-rose-400">-₹{exp.amount.toLocaleString('en-IN')}</p>
                        )
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <ExpenseModal
        open={!!editExpense}
        onClose={() => { setEditExpense(null); fetchData(); }}
        expense={editExpense}
      />
    </motion.div>
  );
}
